import { useState, useMemo, useRef, useCallback } from "react";

export interface LineChartDataPoint {
  label: string;
  tooltipLabel: string;
  value: number;
}

interface LineChartProps {
  data: LineChartDataPoint[];
  title: string;
  valuePrefix?: string;
  valueSuffix?: string;
  color?: string;
  emptyMessage?: string;
  formatValue?: (v: number) => string;
  height?: number;
}

export function LineChart({
  data,
  title,
  valuePrefix = "",
  valueSuffix = "",
  color = "#25D366",
  emptyMessage = "Aucune donnee",
  formatValue,
  height = 180,
}: LineChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const values = useMemo(() => data.map((d) => d.value), [data]);
  const total = useMemo(() => values.reduce((s, v) => s + v, 0), [values]);
  const max = useMemo(() => {
    const dataMax = Math.max(...values, 0);
    if (dataMax === 0) return 1;
    // Round up to a nice number for readable Y-axis
    const magnitude = Math.pow(10, Math.floor(Math.log10(dataMax)));
    const niceSteps = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
    const normalized = dataMax / magnitude;
    const niceMax = (niceSteps.find((s) => s >= normalized) ?? 10) * magnitude;
    return niceMax;
  }, [values]);

  const chartW = 580;
  const chartH = height;

  const points = useMemo(
    () =>
      values.map((v, i) => ({
        x: values.length > 1 ? (i / (values.length - 1)) * chartW : chartW / 2,
        y: chartH - (v / max) * (chartH - 20),
      })),
    [values, max, chartW, chartH]
  );

  const pathD = useMemo(
    () =>
      points.reduce((acc, p, i) => {
        if (i === 0) return `M ${p.x} ${p.y}`;
        const prev = points[i - 1];
        const cpx = (prev.x + p.x) / 2;
        return acc + ` C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
      }, ""),
    [points]
  );

  const fillD = useMemo(
    () =>
      points.length > 0
        ? `${pathD} L ${points[points.length - 1].x} ${chartH} L ${points[0].x} ${chartH} Z`
        : "",
    [pathD, points, chartH]
  );

  // Y-axis labels
  const ySteps = 4;
  const yLabels = useMemo(
    () => Array.from({ length: ySteps + 1 }, (_, i) => (max / ySteps) * i),
    [max]
  );

  // X-axis: show ~6 labels max
  const labeledIndices = useMemo(() => {
    if (data.length <= 7) return data.map((_, i) => i);
    const step = Math.ceil(data.length / 6);
    const indices: number[] = [0];
    for (let i = step; i < data.length - 1; i += step) indices.push(i);
    indices.push(data.length - 1);
    return indices;
  }, [data]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = svgRef.current;
      if (!svg || points.length === 0) return;
      const rect = svg.getBoundingClientRect();
      const svgX = ((e.clientX - rect.left) / rect.width) * (chartW + 40);
      let nearest = 0;
      let minDist = Infinity;
      points.forEach((p, i) => {
        const d = Math.abs(p.x - svgX);
        if (d < minDist) {
          minDist = d;
          nearest = i;
        }
      });
      setHoveredIdx(nearest);
    },
    [points, chartW]
  );

  const fmtValue = useCallback(
    (v: number) => {
      if (formatValue) return formatValue(v);
      return `${valuePrefix}${v.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 4 })}${valueSuffix}`;
    },
    [formatValue, valuePrefix, valueSuffix]
  );

  const fmtYLabel = useCallback(
    (v: number) => {
      if (v >= 1000) return `${valuePrefix}${(v / 1000).toFixed(1)}k${valueSuffix}`;
      if (Number.isInteger(v)) return `${valuePrefix}${v}${valueSuffix}`;
      return `${valuePrefix}${v.toFixed(2)}${valueSuffix}`;
    },
    [valuePrefix, valueSuffix]
  );

  const gradientId = `lineChartFill_${color.replace("#", "")}`;

  return (
    <div className="flex flex-col gap-2">
      {/* Header */}
      <div className="flex flex-row justify-between items-end">
        <div className="flex flex-col">
          <span
            className="text-h2 font-semibold"
            style={{ fontFamily: "Poppins", color: "#374151" }}
          >
            {title}
          </span>
          <span
            className="text-hx font-bold"
            style={{ fontFamily: "Poppins", color }}
          >
            {fmtValue(total)}
          </span>
        </div>
      </div>

      {/* Chart */}
      {total === 0 ? (
        <div
          className="flex items-center justify-center text-h3"
          style={{ height: chartH, color: "#D1D5DB", fontFamily: "Poppins" }}
        >
          {emptyMessage}
        </div>
      ) : (
        <svg
          ref={svgRef}
          viewBox={`0 0 ${chartW + 50} ${chartH + 30}`}
          width="100%"
          className="overflow-visible"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredIdx(null)}
          style={{ cursor: "crosshair" }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.18" />
              <stop offset="100%" stopColor={color} stopOpacity="0.01" />
            </linearGradient>
          </defs>
          {/* Grid lines */}
          {yLabels.map((label, i) => {
            const y = chartH - (label / max) * (chartH - 20);
            return (
              <g key={i}>
                <line
                  x1={0}
                  y1={y}
                  x2={chartW}
                  y2={y}
                  stroke="#f0f0f0"
                  strokeWidth={1}
                />
                <text x={chartW + 8} y={y + 4} fontSize={11} fill="#a3a3a3">
                  {fmtYLabel(label)}
                </text>
              </g>
            );
          })}
          {/* Fill */}
          {fillD && <path d={fillD} fill={`url(#${gradientId})`} />}
          {/* Line */}
          {pathD && (
            <path
              d={pathD}
              fill="none"
              stroke={color}
              strokeWidth={2.5}
            />
          )}
          {/* Hover tooltip */}
          {hoveredIdx !== null && points[hoveredIdx] && (
            <>
              <line
                x1={points[hoveredIdx].x}
                y1={0}
                x2={points[hoveredIdx].x}
                y2={chartH}
                stroke="#e5e7eb"
                strokeWidth={1}
                strokeDasharray="4 2"
              />
              <circle
                cx={points[hoveredIdx].x}
                cy={points[hoveredIdx].y}
                r={5}
                fill={color}
                stroke="white"
                strokeWidth={2}
              />
              <g
                transform={`translate(${Math.min(Math.max(points[hoveredIdx].x - 60, 0), chartW - 120)}, ${points[hoveredIdx].y < 60 ? points[hoveredIdx].y + 15 : points[hoveredIdx].y - 50})`}
              >
                <rect width={120} height={40} rx={8} fill="#1a1a1a" />
                <text
                  x={60}
                  y={16}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#c8c8c8"
                >
                  {data[hoveredIdx].tooltipLabel}
                </text>
                <text
                  x={60}
                  y={32}
                  textAnchor="middle"
                  fontSize={13}
                  fill="white"
                  fontWeight="600"
                >
                  {fmtValue(data[hoveredIdx].value)}
                </text>
              </g>
            </>
          )}
          {/* X labels */}
          {labeledIndices.map((i) => (
            <text
              key={i}
              x={
                values.length > 1
                  ? (i / (values.length - 1)) * chartW
                  : chartW / 2
              }
              y={chartH + 20}
              textAnchor="middle"
              fontSize={11}
              fill="#a3a3a3"
            >
              {data[i].label}
            </text>
          ))}
        </svg>
      )}
    </div>
  );
}
